import TuneIcon from '@mui/icons-material/Tune';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid2';
import Pagination from '@mui/material/Pagination';
import PaginationItem from '@mui/material/PaginationItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { lazy, Suspense, useId, useState } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router';

import { AppButton, GlassPanel } from '@/components/atoms';
import { ErrorState, PanelHeading, StatusState } from '@/components/molecules';
import { VehicleGrid, VehicleSearchForm } from '@/components/vehicles';
import {
  searchDestination,
  SORTS,
  sortLabels,
  type VehicleSearchFormInput,
  type VehicleSearchFormValues,
} from '@/schemas/vehicleSearch';
import { type Page, type VehicleListing, type VehicleSort } from '@/services/vehicles/types';

type Props = {
  formDefaults: VehicleSearchFormInput;
  sort: VehicleSort;
  page: number;
  pageCount: number;
  data: Page<VehicleListing> | undefined;
  isPending: boolean;
  isFetching: boolean;
  error: unknown;
  onRetry: () => void;
};

// Fetched when someone taps Filters; desktop never needs it.
const FiltersDrawer = lazy(() => import('./FiltersDrawer'));

export function CarsResults({
  formDefaults,
  sort,
  page,
  pageCount,
  data,
  isPending,
  isFetching,
  error,
  onRetry,
}: Props) {
  const navigate = useNavigate();
  const location = useLocation();
  const filtersTitleId = useId();
  const [filtersOpen, setFiltersOpen] = useState(false);
  /** Stays true after the first open, so the panel is fetched only once. */
  const [filtersEverOpened, setFiltersEverOpened] = useState(false);

  const withParam = (key: string, value: string | null) => {
    const params = new URLSearchParams(location.search);
    if (value === null) params.delete(key);
    else params.set(key, value);
    if (key !== 'page') params.delete('page');
    const qs = params.toString();
    return `${location.pathname}${qs ? `?${qs}` : ''}`;
  };

  const onSearch = (values: VehicleSearchFormValues) => {
    setFiltersOpen(false);
    const destination = new URL(searchDestination(values), 'http://x');
    if (sort !== 'newest') destination.searchParams.set('sort', sort);
    navigate(`${destination.pathname}${destination.search}`);
  };

  // Remount the form whenever the URL changes, so it always reflects it.
  const form = (
    <VehicleSearchForm
      key={location.pathname + location.search}
      layout="stacked"
      defaultValues={formDefaults}
      onSubmit={onSearch}
      submitLabel="Show results"
    />
  );

  return (
    <Container maxWidth="xl" sx={{ pt: { xs: 3, md: 6 }, pb: { xs: 8, md: 12 } }}>
      <Grid container spacing={8}>
        <Grid size={{ md: 4, lg: 3 }} sx={{ display: { xs: 'none', md: 'block' } }}>
          {/* Frosted rather than smoked: this panel sits on the ordinary page
              background, which is light in light mode. */}
          <GlassPanel
            component="aside"
            aria-labelledby={filtersTitleId}
            sx={{ position: 'sticky', top: 96, p: 6 }}
          >
            <PanelHeading id={filtersTitleId} title="Filter cars" />
            {form}
          </GlassPanel>
        </Grid>

        <Grid size={{ xs: 12, md: 8, lg: 9 }}>
          <Stack
            direction="row"
            spacing={3}
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 6, flexWrap: 'wrap', rowGap: 3 }}
          >
            <Typography
              role="status"
              aria-live="polite"
              color="text.secondary"
              className="tabular-nums"
            >
              {data
                ? `${data.total} ${data.total === 1 ? 'car' : 'cars'} found`
                : isPending
                  ? 'Loading cars…'
                  : ''}
            </Typography>
            <Stack direction="row" spacing={2} alignItems="center" sx={{ minWidth: 0 }}>
              <AppButton
                appVariant="secondary"
                size="small"
                startIcon={<TuneIcon />}
                onClick={() => {
                  setFiltersEverOpened(true);
                  setFiltersOpen(true);
                }}
                sx={{ display: { md: 'none' } }}
              >
                Filters
              </AppButton>
              <TextField
                select
                size="small"
                label="Sort by"
                value={sort}
                onChange={(e) =>
                  navigate(withParam('sort', e.target.value === 'newest' ? null : e.target.value))
                }
                slotProps={{ select: { native: true }, inputLabel: { shrink: true } }}
                // A flat 200px floor plus the Filters button beside it is
                // wider than a 320px phone, and the pair pushed the whole page
                // sideways. The floor is what keeps the longest option
                // ("Newest listings") readable on a roomier screen; the
                // narrowest phones get the width the row can actually spare.
                sx={{ minWidth: { xs: 0, sm: 200 }, flexShrink: 1 }}
              >
                {SORTS.map((s) => (
                  <option key={s} value={s}>
                    {sortLabels[s]}
                  </option>
                ))}
              </TextField>
            </Stack>
          </Stack>

          {error && !data ? (
            <ErrorState error={error} onRetry={onRetry} />
          ) : data && data.total === 0 ? (
            <StatusState
              tone="empty"
              title="No cars match your search yet"
              description="We import to order. Tell us what you want and we'll find it for you."
              action={{ label: "Can't find it? We'll find it", to: '/import-a-car' }}
            />
          ) : (
            <VehicleGrid
              vehicles={data?.items}
              loading={isPending || isFetching}
              headingLevel="h2"
            />
          )}

          {pageCount > 1 && (
            <Pagination
              page={page}
              count={pageCount}
              sx={{ mt: 10, display: 'flex', justifyContent: 'center' }}
              renderItem={(item) => (
                // Only the fields a link needs: MUI's own item props are not
                // compatible with exactOptionalPropertyTypes.
                <PaginationItem
                  component={RouterLink}
                  to={withParam('page', item.page && item.page > 1 ? String(item.page) : null)}
                  type={item.type}
                  page={item.page}
                  selected={item.selected}
                  disabled={item.disabled}
                />
              )}
            />
          )}
        </Grid>
      </Grid>

      {filtersEverOpened && (
        <Suspense fallback={null}>
          <FiltersDrawer open={filtersOpen} onClose={() => setFiltersOpen(false)}>
            {form}
          </FiltersDrawer>
        </Suspense>
      )}
    </Container>
  );
}
